import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import TodaysTasks from './TodaysTasks';
import {
  Users,
  BookOpen,
  GraduationCap,
  Building,
  Clock,
  MapPin,
  CalendarDays,
  Sparkles,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    batches: 0,
    subjects: 0,
    teachers: 0,
    classrooms: 0
  });
  const [todayClasses, setTodayClasses] = useState([]);
  const [todayHolidays, setTodayHolidays] = useState([]);
  const [upcomingStaffHolidays, setUpcomingStaffHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingCache, setClearingCache] = useState(false);
  const { toast } = useToast();

  // Redirect teaching staff to their dedicated dashboard
  if (user && (user.role === 'teacher' || user.role === 'teaching_staff')) {
    return <Navigate to="/teacher-dashboard" replace />;
  }

  useEffect(() => {
    fetchDashboardStats();
    fetchTodayClasses();
    fetchTodayHolidays();
    fetchUpcomingStaffHolidays();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayClasses = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      // Use date filter to get only today's sessions (more efficient and reliable)
      const sessionsResponse = await axios.get(`${API}/sessions`, {
        params: {
          date_from: today,
          date_to: today
        }
      });
      
      const todaySessions = sessionsResponse.data.filter(session => {
        return ['scheduled', 'in-progress'].includes(session.status);
      });
      
      // Sort by start time in ascending order (09:00 onwards)
      const sortedSessions = todaySessions.sort((a, b) => {
        // Extract start time from time_slot (e.g., "09:00-10:30" -> "09:00")
        const timeA = (a.time_slot || '').split('-')[0].trim();
        const timeB = (b.time_slot || '').split('-')[0].trim();
        
        // Convert to comparable format (e.g., "09:00" -> 900, "13:30" -> 1330)
        const getMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const [hours, minutes] = timeStr.split(':').map(Number);
          return (hours * 100) + minutes;
        };
        
        return getMinutes(timeA) - getMinutes(timeB);
      });
      
      setTodayClasses(sortedSessions);
    } catch (error) {
      console.error('Error fetching today\'s classes:', error);
    }
  };

  const fetchTodayHolidays = async () => {
    try {
      const today = new Date();
      const currentYear = today.getFullYear();
      const todayString = today.toISOString().split('T')[0];
      
      const holidaysResponse = await axios.get(`${API}/holidays?year=${currentYear}`);
      
      const todaysHolidays = holidaysResponse.data.filter(holiday => {
        return holiday.date === todayString;
      });
      
      setTodayHolidays(todaysHolidays);
    } catch (error) {
      console.error('Error fetching today\'s holidays:', error);
    }
  };

  const fetchUpcomingStaffHolidays = async () => {
    try {
      const today = new Date();
      const currentYear = today.getFullYear();
      const todayString = today.toISOString().split('T')[0];
      
      // Get next 30 days of staff holidays
      const thirtyDaysLater = new Date(today.getTime() + (30 * 24 * 60 * 60 * 1000));
      const thirtyDaysString = thirtyDaysLater.toISOString().split('T')[0];
      
      const holidaysResponse = await axios.get(`${API}/holidays?year=${currentYear}`);
      
      const upcomingStaffHolidays = holidaysResponse.data.filter(holiday => {
        return holiday.is_staff_holiday && 
               holiday.date > todayString && 
               holiday.date <= thirtyDaysString;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setUpcomingStaffHolidays(upcomingStaffHolidays);
    } catch (error) {
      console.error('Error fetching upcoming staff holidays:', error);
    }
  };

  const handleClearCache = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      '🗑️ Clear All Cache?\n\n' +
      'This will:\n' +
      '• Clear browser localStorage and sessionStorage\n' +
      '• Clear React component state cache\n' +
      '• Trigger backend cache clearing (if available)\n' +
      '• Force refresh all data\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    setClearingCache(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Try to clear backend cache if endpoint exists
      try {
        await axios.post(`${API}/admin/clear-cache`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Backend cache cleared",
          description: "✅ Server cache has been cleared successfully",
        });
      } catch (error) {
        console.log('Backend cache endpoint not available (expected)');
        // This is expected if endpoint doesn't exist yet
      }

      // Step 2: Clear browser storage (except auth token)
      const authToken = localStorage.getItem('token');
      
      // Clear localStorage except token
      Object.keys(localStorage).forEach(key => {
        if (key !== 'token') {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear any React Query cache if present
      if (window.queryClient) {
        window.queryClient.clear();
      }

      // Step 3: Force reload all dashboard data
      setLoading(true);
      await Promise.all([
        fetchDashboardStats(),
        fetchTodayClasses(),
        fetchTodayHolidays(),
        fetchUpcomingStaffHolidays()
      ]);

      toast({
        title: "Cache cleared successfully!",
        description: "🎉 All cache cleared and data refreshed.",
      });

    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: "Cache clearing completed",
        description: "⚠️ Cache cleared with some warnings. Data has been refreshed.",
        variant: "destructive"
      });
      
      // Still reload data even if backend call failed
      setLoading(true);
      await Promise.all([
        fetchDashboardStats(),
        fetchTodayClasses(),
        fetchTodayHolidays(),
        fetchUpcomingStaffHolidays()
      ]);
    } finally {
      setLoading(false);
      setClearingCache(false);
    }
  };

  const statCards = [
    {
      title: 'Total Batches',
      value: stats.batches,
      icon: Users,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      title: 'Total Students',
      value: stats.total_students || 0,
      icon: Users,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-700'
    },
    {
      title: 'Subjects',
      value: stats.subjects,
      icon: BookOpen,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700'
    },
    {
      title: 'Teachers',
      value: stats.teachers,
      icon: GraduationCap,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700'
    },
    {
      title: 'Classrooms',
      value: stats.classrooms,
      icon: Building,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome to Smart Manager - your intelligent management solution</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Clear Cache Button */}
          <Button
            onClick={handleClearCache}
            disabled={clearingCache}
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
          >
            {clearingCache ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            {clearingCache ? 'Clearing...' : 'Clear Cache'}
          </Button>
          
          {/* Date/Time Display */}
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>{new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover-lift card-enhanced" data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.textColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Today's Tasks - Only for Admin & Super Admin */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && (
        <TodaysTasks />
      )}

      {/* Spacer for better layout */}
      <div className="mb-6"></div>

      {/* Today's Classes */}
      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
            Today's Classes ({todayClasses.length})
            {todayHolidays.length > 0 && (
              <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700">
                <Sparkles className="w-3 h-3 mr-1" />
                Holiday
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Show holidays first if any */}
          {todayHolidays.length > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
              <div className="flex items-center mb-2">
                <Sparkles className="w-5 h-5 text-orange-600 mr-2" />
                <h4 className="font-semibold text-orange-800">Today's Holiday{todayHolidays.length > 1 ? 's' : ''}</h4>
              </div>
              {todayHolidays.map((holiday, index) => (
                <div key={holiday.id} className="mb-2 last:mb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-orange-900">{holiday.name}</p>
                        {holiday.is_staff_holiday && (
                          <Badge className="bg-red-100 text-red-700 border-red-300 text-xs">
                            <Sparkles className="w-2 h-2 mr-1" />
                            Staff Off
                          </Badge>
                        )}
                      </div>
                      {holiday.description && (
                        <p className="text-sm text-orange-700">{holiday.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-orange-700 border-orange-300 capitalize">
                      {holiday.holiday_type}
                    </Badge>
                  </div>
                </div>
              ))}
              <p className="text-sm text-orange-600 mt-2 italic">
                {todayHolidays.some(h => h.is_staff_holiday) 
                  ? (todayClasses.length > 0 
                     ? `${todayClasses.length} classes scheduled on staff holiday - verify if intentional.`
                     : 'Staff holiday - No classes scheduled.')
                  : (todayClasses.length > 0 
                     ? `${todayClasses.length} classes are still scheduled despite the holiday.`
                     : 'No classes scheduled - enjoy the holiday!')
                }
              </p>
            </div>
          )}

          {todayClasses.length === 0 && todayHolidays.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No classes scheduled for today</p>
              <p className="text-sm text-gray-400 mt-1">All clear for administrative work!</p>
            </div>
          ) : todayClasses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todayClasses.map((session, index) => (
                <div key={session.id} className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:shadow-md transition-shadow">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <h4 className="font-semibold text-gray-900 text-sm">{session.subject_name}</h4>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        {session.status}
                      </span>
                    </div>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{session.time_slot}</span>
                      </div>
                      <div className="flex items-center">
                        <GraduationCap className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{session.teacher_name}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{session.classroom_name}</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{session.batch_name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Upcoming Staff Holidays */}
      {upcomingStaffHolidays.length > 0 && (
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2 text-green-600" />
              Upcoming Staff Holidays ({upcomingStaffHolidays.length})
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                Next 30 Days
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingStaffHolidays.map((holiday, index) => (
                <div key={holiday.id} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-green-900">{holiday.name}</h4>
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                              <Sparkles className="w-2 h-2 mr-1" />
                              Staff Off
                            </Badge>
                          </div>
                          {holiday.description && (
                            <p className="text-sm text-green-700 mt-1">{holiday.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-900">
                          {new Date(holiday.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-green-600">
                          {Math.ceil((new Date(holiday.date) - new Date()) / (1000 * 60 * 60 * 24))} days away
                        </div>
                      </div>
                      <Badge variant="outline" className="text-green-700 border-green-300 capitalize text-xs">
                        {holiday.holiday_type}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 text-center">
                <Sparkles className="w-4 h-4 inline mr-1" />
                <strong>Staff Holidays:</strong> You have {upcomingStaffHolidays.length} upcoming day{upcomingStaffHolidays.length !== 1 ? 's' : ''} off in the next 30 days.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;